import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import NotificationDropdown from '../shared/NotificationDropdown';
import {
  LayoutDashboard,
  Bell,
  Users,
  UserPlus,
  UserCheck,
  Settings as SettingsIcon,
  LogOut,
  GitBranch,
  Menu,
  X,
  ChevronLeft,
  BarChart3,
  FileText,
  Shield,
  GraduationCap,
  DollarSign,
} from 'lucide-react';
import logo from '../../assets/pictures/logo.png';

const ADMIN_MENU_ITEMS = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  {
    path: '/admin/service-requests',
    icon: FileText,
    label: 'Service Requests',
  },
  { path: '/admin/students', icon: Users, label: 'Manage Students' },
  { path: '/admin/counselors', icon: UserCheck, label: 'Manage Counselors' },
  { path: '/admin/representatives', icon: UserPlus, label: 'Representative Management' },
  { path: '/admin/admissions', icon: GraduationCap, label: 'Admissions' },
  { path: '/admin/assignments', icon: GitBranch, label: 'Assignments' },
  { path: '/admin/payment-requests', icon: DollarSign, label: 'Payment Requests' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/reports', icon: BarChart3, label: 'Reports' },
  { path: '/admin/settings', icon: SettingsIcon, label: 'Settings' },
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className="h-screen overflow-hidden bg-[#FAF7F1]"
      data-testid="admin-layout"
    >
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Fixed Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 h-screen flex flex-col bg-white border-r border-stone-200/80 shadow-sm transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
        data-testid="admin-sidebar"
      >
        {/* Logo Header */}
        <div
          className={`h-[64px] px-4 border-b border-stone-100 flex items-center flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          <Link to="/admin" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="relative p-2 transition-shadow">
                <img
                  src={logo}
                  alt="Fly8"
                  className="w-12 h-12 object-contain"
                />
              </div>
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <span className="text-[13px] font-extrabold tracking-widest uppercase text-[#F2575F]">
                  Admin Portal
                </span>
              </div>
            )}
          </Link>

          <button
            className={`hidden lg:flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-stone-100 transition-all duration-200 ${collapsed ? 'absolute right-2' : ''}`}
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft
              className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            />
          </button>

          <button
            className="lg:hidden p-1.5 hover:bg-stone-100 rounded-lg text-gray-400 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav
          className="flex-1 px-3 py-2 overflow-hidden "
          data-testid="admin-menu"
        >
          <div className="space-y-2">
            {ADMIN_MENU_ITEMS.map(item => {
              const Icon = item.icon;
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group relative overflow-hidden flex justify-center  items-center gap-3 px-3 py-[6px] rounded-xl text-base font-medium transition-all duration-200 before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-[#F2575F] before:translate-x-full before:transition-transform before:duration-300 before:ease-out hover:before:translate-x-0 hover:shadow-md hover:shadow-[#F2575F]/20 ${
                    isActive
                      ? 'bg-[#F2575F] text-white shadow-md shadow-[#F2575F]/20'
                      : 'text-gray-600 hover:text-white'
                  } ${collapsed ? 'justify-center px-2.5' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  title={collapsed ? item.label : undefined}
                  data-testid={`admin-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 relative z-10 transition-all duration-200 ${
                      isActive
                        ? 'bg-white/20 shadow-md shadow-white/20'
                        : 'bg-red-50 shadow-sm shadow-red-100/80 group-hover:bg-white/20 group-hover:shadow-[0_0_14px_rgba(255,255,255,0.50)]'
                    }`}
                  >
                    <Icon
                      className={`w-[17px] h-[17px] transition-colors duration-200 ${
                        isActive
                          ? 'text-white'
                          : 'text-red-500 group-hover:text-white'
                      }`}
                    />
                  </div>
                  {!collapsed && (
                    <span className="text-[14px] flex-1 relative z-10">
                      {item.label}
                    </span>
                  )}
                  {!collapsed && item.badge && (
                    <Badge className="bg-[#F2575F] text-white text-[10px] px-1.5 py-0.5 font-bold rounded-full relative z-10">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Section */}
        <div
          className={`flex-shrink-0 p-3 border-t border-stone-100 ${collapsed ? 'flex flex-col items-center' : ''}`}
        >
          {!collapsed && (
            <div className="mb-3 p-3 rounded-xl bg-stone-50 border border-stone-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F2575F] rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm">
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Shield className="w-3 h-3 text-[#F2575F]" />
                    <p className="text-[11px] font-medium text-[#F2575F]">
                      Super Admin
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {collapsed && (
            <div className="mb-3">
              <div className="w-10 h-10 bg-[#F2575F] rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </div>
            </div>
          )}
          <Button
            onClick={logout}
            variant="ghost"
            size="sm"
            data-testid="admin-logout-button"
            className={`
    w-full
    flex items-center
    border border-red-200/40
    text-red-500
    hover:text-red-600
    hover:bg-red-50
    active:bg-red-100
    transition-all duration-200 ease-in-out
    rounded-xl shadow-sm shadow-red-300/20
    ${collapsed ? 'p-2.5 justify-center' : 'px-3 py-2 justify-start'}
  `}
          >
            <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-2'}`} />
            {!collapsed && (
              <span className="text-[13px] font-semibold tracking-wide">
                Sign Out
              </span>
            )}
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        className={`h-screen flex flex-col transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}
      >
        {/* Top Header Bar */}
        <header
          className="h-[64px] flex-shrink-0 bg-white/80 backdrop-blur-md border-b border-stone-200/60 px-4 lg:px-8 flex items-center sticky top-0 z-30"
          data-testid="admin-header"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-stone-100 rounded-xl text-gray-500 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h2 className="text-lg font-bold font-outfit text-gray-900">
                    Admin Portal
                  </h2>
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-[#F2575F] text-white rounded-md uppercase tracking-wider">
                    Pro
                  </span>
                </div>
                <p className="text-xs text-gray-400 hidden sm:block">
                  International Student Services Platform
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-medium text-emerald-700">
                  All Systems Online
                </span>
              </div>
              <NotificationDropdown />
            </div>
          </div>
        </header>

        {/* Scrollable Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
